import _ from 'lodash'
import { INTENT_PATTERNS, CONTEXT_TYPES, getContextTypesForIntents } from './aiIntentPatterns'

/**
 * AI Context Provider Service
 * Dynamically provides relevant site context based on user query intent
 */
class AIContextProvider {
  constructor(store) {
    this.store = store
    this.contextCache = new Map()
    this.cacheTimeout = 5 * 60 * 1000 // 5 minutes
  }

  /**
   * Get comprehensive context based on user message
   * @param {string} userMessage - The user's message to analyze
   * @param {Object} options - Additional options like version, comments
   * @returns {Object} Structured context object
   */
  async getContext(userMessage, options = {}) {
    try {
      const intents = this.analyzeIntent(userMessage)
      const contextTypes = getContextTypesForIntents(intents)
      
      const context = {
        site: this.getSiteContext(),
        intents: intents,
        contextTypes: contextTypes,
        timestamp: new Date().toISOString()
      }

      // Add context based on detected intents
      for (const contextType of contextTypes) {
        try {
          const contextData = await this.getContextByType(contextType)
          if (contextData) {
            context[contextType] = contextData
          }
        } catch (error) {
          console.error(`Error loading context type ${contextType}:`, error)
          // Continue with other context types even if one fails
        }
      }

      // Always include recent conversation for context
      if (options.comments) {
        try {
          context.conversation = this.getConversationContext(options.comments)
        } catch (error) {
          console.error('Error loading conversation context:', error)
        }
      }

      return context
    } catch (error) {
      console.error('Error in getContext:', error)
      // Return a minimal context to prevent complete failure
      return {
        site: this.getSiteContext(),
        intents: [],
        contextTypes: [],
        timestamp: new Date().toISOString(),
        error: 'Failed to load full context'
      }
    }
  }

  /**
   * Get context data by type
   * @param {string} contextType - Type of context to retrieve
   * @returns {Object|null} Context data or null if not available
   */
  async getContextByType(contextType) {
    switch (contextType) {
      case 'versions':
        return await this.getVersionsContext()
      case 'usage':
        return this.getUsageContext()
      case 'apps':
        return await this.getAppsContext()
      case 'deployment':
        return this.getDeploymentContext()
      case 'users':
        return this.getUsersContext()
      case 'forms':
        return await this.getFormsContext()
      case 'security':
        return this.getSecurityContext()
      case 'domain':
        return this.getDomainContext()
      default:
        return null
    }
  }

  /**
   * Analyze user intent from message using pattern matching
   * @param {string} message - User message
   * @returns {Array} Array of detected intents
   */
  analyzeIntent(message) {
    const lowerMessage = message.toLowerCase()
    const intents = []

    // Check each intent pattern
    Object.entries(INTENT_PATTERNS).forEach(([intent, pattern]) => {
      const hasKeyword = pattern.keywords.some(keyword => 
        lowerMessage.includes(keyword)
      )
      if (hasKeyword) {
        intents.push(intent)
      }
    })

    return intents
  }

  /**
   * Get basic site context
   * @returns {Object} Site information
   */
  getSiteContext() {
    const site = this.store.state.sites?.active || {}
    
    return {
      name: site.site_name,
      url: site.url,
      type: site.kind,
      mode: site.mode,
      status: site.currentVersionStatus,
      currentVersion: site.currentVersionScopedId,
      lastUpdated: site.updatedAt ? new Date(site.updatedAt).toLocaleString() : null,
      compiler: site.compiler,
      ssl: site.sslEnabled,
      autoDeploy: {
        github: site.githubAutodeploy,
        dropbox: site.dropboxAutodeploy,
        bitbucket: site.bitbucketAutodeploy
      },
      build: {
        command: site.buildCommand,
        folder: site.buildFolder
      }
    }
  }

  /**
   * Get versions context for change tracking
   * @returns {Object} Versions information
   */
  async getVersionsContext() {
    const cacheKey = 'versions'
    const cached = this.getCachedContext(cacheKey)
    if (cached) return cached

    const versions = this.store.state.versions?.list || []
    const site = this.store.state.sites?.active

    // Get recent versions with detailed change information
    const recentVersions = versions
      .slice(0, 10) // Last 10 versions
      .map(version => {
        try {
          // Debug the version data structure
          this.debugVersion(version)
          
          // Ensure we have valid arrays for file lists
          const addedFiles = Array.isArray(version.added) ? version.added : []
          const removedFiles = Array.isArray(version.removed) ? version.removed : []
          const modifiedFiles = Array.isArray(version.modified) ? version.modified : []
          
          return {
            id: version.id,
            scopedId: version.scopedId,
            description: version.description || '',
            createdAt: version.createdAt,
            status: version.deployStatus || 'unknown',
            percentDeployed: version.percentDeployed || 0,
            uploadFileSize: version.uploadFileSize || 0,
            // File changes
            addedFiles,
            removedFiles,
            modifiedFiles,
            // Categorized files
            categorizedChanges: {
              added: this.categorizeFiles(addedFiles),
              removed: this.categorizeFiles(removedFiles),
              modified: this.categorizeFiles(modifiedFiles)
            },
            // Detailed diff information
            diff: {
              added: Array.isArray(version.diff?.added) ? version.diff.added : [],
              deleted: Array.isArray(version.diff?.deleted) ? version.diff.deleted : [],
              modified: Array.isArray(version.diff?.modified) ? version.diff.modified : []
            },
            // Summary statistics
            totalChanges: addedFiles.length + removedFiles.length + modifiedFiles.length
          }
        } catch (error) {
          console.error('Error processing version:', version, error)
          // Return a safe fallback version object
          return {
            id: version.id || 'unknown',
            scopedId: version.scopedId || 0,
            description: 'Error processing version data',
            createdAt: version.createdAt || new Date(),
            status: 'error',
            percentDeployed: 0,
            uploadFileSize: 0,
            addedFiles: [],
            removedFiles: [],
            modifiedFiles: [],
            categorizedChanges: {
              added: {},
              removed: {},
              modified: {}
            },
            diff: {
              added: [],
              deleted: [],
              modified: []
            },
            totalChanges: 0
          }
        }
      })

    // Calculate overall change statistics
    const totalAdded = recentVersions.reduce((sum, v) => sum + v.addedFiles.length, 0)
    const totalRemoved = recentVersions.reduce((sum, v) => sum + v.removedFiles.length, 0)
    const totalModified = recentVersions.reduce((sum, v) => sum + v.modifiedFiles.length, 0)

    const context = {
      totalVersions: versions.length,
      currentVersion: site?.currentVersionScopedId,
      recentVersions,
      lastDeploy: versions.length > 0 ? versions[0].createdAt : null,
      changeStatistics: {
        totalAdded,
        totalRemoved,
        totalModified,
        totalChanges: totalAdded + totalRemoved + totalModified
      }
    }

    this.setCachedContext(cacheKey, context)
    return context
  }

  /**
   * Get usage and bandwidth context
   * @returns {Object} Usage information
   */
  getUsageContext() {
    const site = this.store.state.sites?.active
    if (!site?.bandwidth) return null

    return {
      current: {
        today: site.bandwidth.today,
        week: site.bandwidth.week,
        month: site.bandwidth.month,
        thisMonth: site.bandwidth.thisMonth
      },
      historical: site.bandwidth.previousMonths?.slice(0, 6) || [],
      trends: this.calculateUsageTrends(site.bandwidth),
      formatted: {
        today: this.formatBandwidth(site.bandwidth.today),
        week: this.formatBandwidth(site.bandwidth.week),
        month: this.formatBandwidth(site.bandwidth.month),
        thisMonth: this.formatBandwidth(site.bandwidth.thisMonth)
      }
    }
  }

  /**
   * Get apps and plugins context
   * @returns {Object} Apps information
   */
  async getAppsContext() {
    const cacheKey = 'apps'
    const cached = this.getCachedContext(cacheKey)
    if (cached) return cached

    const siteApps = this.store.state.site_app?.list || []
    const site = this.store.state.sites?.active

    const apps = siteApps.map(app => ({
      id: app.id,
      name: app.name,
      type: app.type,
      status: app.status,
      deployed: app.deployed,
      deploying: app.deploying,
      lastDeployed: app.lastDeployed
    }))

    const context = {
      totalApps: apps.length,
      deployedApps: apps.filter(app => app.deployed).length,
      deployingApps: apps.filter(app => app.deploying).length,
      apps
    }

    this.setCachedContext(cacheKey, context)
    return context
  }

  /**
   * Get deployment context
   * @returns {Object} Deployment information
   */
  getDeploymentContext() {
    const site = this.store.state.sites?.active
    if (!site) return null

    return {
      status: site.currentVersionStatus,
      currentVersion: site.currentVersionScopedId,
      deployCount: site.deployCount,
      autoDeploy: {
        github: site.githubAutodeploy,
        dropbox: site.dropboxAutodeploy,
        bitbucket: site.bitbucketAutodeploy
      },
      compiler: site.compiler,
      buildCommand: site.buildCommand,
      buildFolder: site.buildFolder,
      ssl: site.sslEnabled
    }
  }

  /**
   * Get users and access context
   * @returns {Object} Users information
   */
  getUsersContext() {
    const siteUsers = this.store.state.site_user?.list || []
    const currentUser = this.store.state.user?.current

    return {
      totalUsers: siteUsers.length,
      currentUser: currentUser ? {
        id: currentUser.id,
        name: currentUser.name,
        email: currentUser.email
      } : null,
      users: siteUsers.map(su => ({
        id: su.id,
        userId: su.user_id,
        role: su.role,
        permissions: su.permissions
      }))
    }
  }

  /**
   * Get conversation context
   * @param {Array} comments - Recent comments
   * @returns {Object} Conversation information
   */
  getConversationContext(comments) {
    const recentComments = comments
      .slice(0, 5)
      .reverse()
      .map(comment => ({
        author: comment.is_ai_response ? 'Forge AI' : comment.userName,
        message: comment.message,
        timestamp: comment.createdAt
      }))

    return {
      totalComments: comments.length,
      recentComments
    }
  }

  /**
   * Get forms context
   * @returns {Object} Forms information
   */
  async getFormsContext() {
    const cacheKey = 'forms'
    const cached = this.getCachedContext(cacheKey)
    if (cached) return cached

    const forms = this.store.state.form?.list || []
    const site = this.store.state.sites?.active

    const context = {
      totalForms: forms.length,
      forms: forms.map(form => ({
        id: form.id,
        name: form.name,
        status: form.status,
        submissions: form.submissions || 0,
        createdAt: form.createdAt
      }))
    }

    this.setCachedContext(cacheKey, context)
    return context
  }

  /**
   * Get security context
   * @returns {Object} Security information
   */
  getSecurityContext() {
    const site = this.store.state.sites?.active
    if (!site) return null

    return {
      ssl: {
        enabled: site.sslEnabled,
        forced: site.forceSSLEnabled,
        useSSL: site.use_ssl
      },
      security: {
        phishing: site.phishing,
        squish: site.squish
      }
    }
  }

  /**
   * Get domain context
   * @returns {Object} Domain information
   */
  getDomainContext() {
    const site = this.store.state.sites?.active
    if (!site) return null

    return {
      url: site.url,
      customDomain: site.url !== `${site.site_name}.getforge.com`,
      subdomain: site.site_name,
      sslEnabled: site.sslEnabled
    }
  }

  /**
   * Calculate usage trends
   * @param {Object} bandwidth - Bandwidth data
   * @returns {Object} Trend information
   */
  calculateUsageTrends(bandwidth) {
    if (!bandwidth.previousMonths || bandwidth.previousMonths.length < 2) {
      return null
    }

    const recent = bandwidth.previousMonths.slice(0, 2)
    const current = recent[0]?.bandwidth || 0
    const previous = recent[1]?.bandwidth || 0
    
    const change = current - previous
    const percentageChange = previous > 0 ? (change / previous) * 100 : 0

    // Calculate trend based on multiple data points for better accuracy
    let trend = 'stable'
    if (bandwidth.previousMonths.length >= 3) {
      const last3Months = bandwidth.previousMonths.slice(0, 3).map(m => m.bandwidth)
      const avgChange = (last3Months[0] - last3Months[2]) / 2
      
      if (avgChange > current * 0.1) { // 10% threshold
        trend = 'increasing'
      } else if (avgChange < -current * 0.1) {
        trend = 'decreasing'
      }
    } else {
      // Fallback to simple comparison
      trend = change > 0 ? 'increasing' : change < 0 ? 'decreasing' : 'stable'
    }

    return {
      change,
      percentageChange,
      trend,
      formatted: {
        change: this.formatBandwidth(Math.abs(change)),
        percentageChange: Math.abs(percentageChange).toFixed(1)
      }
    }
  }

  /**
   * Get cached context
   * @param {string} key - Cache key
   * @returns {Object|null} Cached context or null
   */
  getCachedContext(key) {
    const cached = this.contextCache.get(key)
    if (!cached) return null

    if (Date.now() - cached.timestamp > this.cacheTimeout) {
      this.contextCache.delete(key)
      return null
    }

    return cached.data
  }

  /**
   * Set cached context
   * @param {string} key - Cache key
   * @param {Object} data - Context data
   */
  setCachedContext(key, data) {
    this.contextCache.set(key, {
      data,
      timestamp: Date.now()
    })
  }

  /**
   * Clear context cache
   */
  clearCache() {
    this.contextCache.clear()
  }

  /**
   * Debug method to log version data structure
   * @param {Object} version - Version object to debug
   */
  debugVersion(version) {
    console.log('Debug version data:', {
      id: version.id,
      scopedId: version.scopedId,
      added: version.added,
      removed: version.removed,
      modified: version.modified,
      addedType: typeof version.added,
      removedType: typeof version.removed,
      modifiedType: typeof version.modified,
      isAddedArray: Array.isArray(version.added),
      isRemovedArray: Array.isArray(version.removed),
      isModifiedArray: Array.isArray(version.modified)
    })
  }

  /**
   * Format file size in human readable format
   * @param {number} bytes - File size in bytes
   * @returns {string} Formatted file size
   */
  formatFileSize(bytes) {
    if (!bytes || bytes === 0) return '0 B'
    
    const k = 1024
    const sizes = ['B', 'KB', 'MB', 'GB']
    const i = Math.floor(Math.log(bytes) / Math.log(k))
    
    return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i]
  }

  /**
   * Format bandwidth usage in human readable format
   * @param {number} bytes - Bandwidth in bytes
   * @returns {string} Formatted bandwidth
   */
  formatBandwidth(bytes) {
    if (!bytes || bytes === 0) return '0 MB'
    
    // Convert to MB for bandwidth display (more appropriate for web usage)
    const mb = bytes / (1024 * 1024)
    
    if (mb < 1) {
      return `${(bytes / 1024).toFixed(2)} KB`
    } else if (mb < 1024) {
      return `${mb.toFixed(2)} MB`
    } else {
      return `${(mb / 1024).toFixed(2)} GB`
    }
  }

  /**
   * Categorize files by type
   * @param {Array} files - Array of file paths
   * @returns {Object} Categorized files
   */
  categorizeFiles(files) {
    const categories = {
      html: [],
      css: [],
      js: [],
      images: [],
      documents: [],
      config: [],
      other: []
    }

    const extensions = {
      html: ['.html', '.htm', '.xhtml'],
      css: ['.css', '.scss', '.sass', '.less'],
      js: ['.js', '.jsx', '.ts', '.tsx'],
      images: ['.jpg', '.jpeg', '.png', '.gif', '.svg', '.webp', '.ico'],
      documents: ['.pdf', '.doc', '.docx', '.txt', '.md'],
      config: ['.json', '.xml', '.yaml', '.yml', '.env', '.config']
    }

    // Ensure files is an array and handle non-string values
    if (!Array.isArray(files)) {
      console.warn('categorizeFiles: files parameter is not an array:', files)
      return categories
    }

    files.forEach(file => {
      // Ensure file is a string
      if (typeof file !== 'string') {
        console.warn('categorizeFiles: skipping non-string file:', file)
        categories.other.push(String(file))
        return
      }

      // Handle files without extensions
      const lastDotIndex = file.lastIndexOf('.')
      if (lastDotIndex === -1) {
        categories.other.push(file)
        return
      }

      const ext = file.toLowerCase().substring(lastDotIndex)
      let categorized = false

      for (const [category, exts] of Object.entries(extensions)) {
        if (exts.includes(ext)) {
          categories[category].push(file)
          categorized = true
          break
        }
      }

      if (!categorized) {
        categories.other.push(file)
      }
    })

    return categories
  }

  /**
   * Format context for AI prompt
   * @param {Object} context - Context object
   * @returns {string} Formatted prompt string
   */
  formatContextForPrompt(context) {
    try {
      let prompt = 'Site Context:\n'
      
      // Basic site info
      if (context.site) {
        prompt += `Site: ${context.site.name || 'Unknown'} (${context.site.url || 'Unknown'})\n`
        prompt += `Type: ${context.site.type || 'Unknown'}, Mode: ${context.site.mode || 'Unknown'}\n`
        prompt += `Current Version: ${context.site.currentVersion || 'Unknown'}, Status: ${context.site.status || 'Unknown'}\n`
        prompt += `Last Updated: ${context.site.lastUpdated || 'Unknown'}\n\n`
      }

    // Versions and changes
    if (context.versions) {
      prompt += `Version History:\n`
      prompt += `Total Versions: ${context.versions.totalVersions}\n`
      prompt += `Current Version: ${context.versions.currentVersion}\n`
      prompt += `Overall Changes (last 10 versions): ${context.versions.changeStatistics.totalAdded} files added, ${context.versions.changeStatistics.totalRemoved} files removed, ${context.versions.changeStatistics.totalModified} files modified\n\n`
      
      if (context.versions.recentVersions.length > 0) {
        prompt += `Recent Version Details:\n`
        context.versions.recentVersions.forEach(version => {
          prompt += `Version ${version.scopedId} (${version.createdAt.toLocaleDateString()}):\n`
          prompt += `  Status: ${version.status}, Deployed: ${version.percentDeployed}%\n`
          prompt += `  Description: ${version.description || 'No description'}\n`
          prompt += `  File Size: ${this.formatFileSize(version.uploadFileSize)}\n`
          prompt += `  Changes: ${version.addedFiles.length} added, ${version.removedFiles.length} removed, ${version.modifiedFiles.length} modified\n`
          
          // Add categorized file details if available
          if (version.addedFiles && version.addedFiles.length > 0) {
            prompt += `  Added Files (${version.addedFiles.length} total):\n`
            const addedCategories = version.categorizedChanges?.added || {}
            Object.entries(addedCategories).forEach(([category, files]) => {
              if (Array.isArray(files) && files.length > 0) {
                prompt += `    ${category.toUpperCase()}: ${files.length} files\n`
                files.slice(0, 3).forEach(file => {
                  prompt += `      + ${file}\n`
                })
                if (files.length > 3) {
                  prompt += `      ... and ${files.length - 3} more ${category} files\n`
                }
              }
            })
          }
          
          if (version.removedFiles && version.removedFiles.length > 0) {
            prompt += `  Removed Files (${version.removedFiles.length} total):\n`
            const removedCategories = version.categorizedChanges?.removed || {}
            Object.entries(removedCategories).forEach(([category, files]) => {
              if (Array.isArray(files) && files.length > 0) {
                prompt += `    ${category.toUpperCase()}: ${files.length} files\n`
                files.slice(0, 3).forEach(file => {
                  prompt += `      - ${file}\n`
                })
                if (files.length > 3) {
                  prompt += `      ... and ${files.length - 3} more ${category} files\n`
                }
              }
            })
          }
          
          if (version.modifiedFiles && version.modifiedFiles.length > 0) {
            prompt += `  Modified Files (${version.modifiedFiles.length} total):\n`
            const modifiedCategories = version.categorizedChanges?.modified || {}
            Object.entries(modifiedCategories).forEach(([category, files]) => {
              if (Array.isArray(files) && files.length > 0) {
                prompt += `    ${category.toUpperCase()}: ${files.length} files\n`
                files.slice(0, 3).forEach(file => {
                  prompt += `      ~ ${file}\n`
                })
                if (files.length > 3) {
                  prompt += `      ... and ${files.length - 3} more ${category} files\n`
                }
              }
            })
          }
          
          prompt += '\n'
        })
      }
      prompt += '\n'
    }

    // Usage and bandwidth
    if (context.usage) {
      prompt += `Usage Statistics:\n`
      prompt += `Today: ${context.usage.formatted.today}\n`
      prompt += `This Week: ${context.usage.formatted.week}\n`
      prompt += `This Month: ${context.usage.formatted.thisMonth}\n`
      
      if (context.usage.trends) {
        const trend = context.usage.trends.trend
        const change = context.usage.trends.formatted.change
        const percentage = context.usage.trends.formatted.percentageChange
        
        prompt += `Trend: ${trend} (${change} change, ${percentage}%)\n`
      }
      
      // Add historical context if available
      if (context.usage.historical.length > 0) {
        prompt += `Historical Usage (last 6 months):\n`
        context.usage.historical.forEach(month => {
          prompt += `  ${month.month}: ${this.formatBandwidth(month.bandwidth)}\n`
        })
      }
      
      prompt += '\n'
    }

    // Apps and plugins
    if (context.apps) {
      prompt += `Installed Apps:\n`
      prompt += `Total: ${context.apps.totalApps}, Deployed: ${context.apps.deployedApps}\n`
      if (context.apps.apps.length > 0) {
        context.apps.apps.forEach(app => {
          prompt += `- ${app.name} (${app.type}): ${app.status}\n`
        })
      }
      prompt += '\n'
    }

    // Deployment info
    if (context.deployment) {
      prompt += `Deployment Status:\n`
      prompt += `Status: ${context.deployment.status}\n`
      prompt += `Compiler: ${context.deployment.compiler}\n`
      prompt += `Auto-deploy: ${Object.values(context.deployment.autoDeploy).some(Boolean) ? 'Enabled' : 'Disabled'}\n`
      prompt += '\n'
    }

    // Forms
    if (context.forms) {
      prompt += `Forms:\n`
      prompt += `Total Forms: ${context.forms.totalForms}\n`
      if (context.forms.forms.length > 0) {
        context.forms.forms.forEach(form => {
          prompt += `- ${form.name}: ${form.submissions} submissions\n`
        })
      }
      prompt += '\n'
    }

    // Security
    if (context.security) {
      prompt += `Security Settings:\n`
      prompt += `SSL: ${context.security.ssl.enabled ? 'Enabled' : 'Disabled'}\n`
      if (context.security.ssl.enabled) {
        prompt += `Force SSL: ${context.security.ssl.forced ? 'Yes' : 'No'}\n`
      }
      prompt += `Phishing Protection: ${context.security.security.phishing ? 'Enabled' : 'Disabled'}\n`
      prompt += `Squish: ${context.security.security.squish ? 'Enabled' : 'Disabled'}\n`
      prompt += '\n'
    }

    // Domain
    if (context.domain) {
      prompt += `Domain Configuration:\n`
      prompt += `URL: ${context.domain.url}\n`
      prompt += `Custom Domain: ${context.domain.customDomain ? 'Yes' : 'No'}\n`
      prompt += `Subdomain: ${context.domain.subdomain}\n`
      prompt += '\n'
    }

    // Users
    if (context.users) {
      prompt += `Users and Access:\n`
      prompt += `Total Users: ${context.users.totalUsers}\n`
      if (context.users.currentUser) {
        prompt += `Current User: ${context.users.currentUser.name} (${context.users.currentUser.email})\n`
      }
      prompt += '\n'
    }

    // Recent conversation
    if (context.conversation) {
      prompt += `Recent Conversation:\n`
      context.conversation.recentComments.forEach(comment => {
        prompt += `${comment.author}: ${comment.message}\n`
      })
      prompt += '\n'
    }

          return prompt
    } catch (error) {
      console.error('Error formatting context for prompt:', error)
      return 'Site Context:\nError: Failed to format context properly\n\n'
    }
  }
}

export default AIContextProvider 